import { FC, useEffect, useState } from 'react';
import { Box, Flex, useToast, VStack, Stat, StatLabel, StatNumber, useColorModeValue, Grid, GridItem, Center, Spinner, Text } from '@chakra-ui/react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, CartesianGrid, Legend } from 'recharts';
import { Panel, AnimatedCard, containerVariants, cardVariants } from '@bemap/ui';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { getPartnerDashboard } from '@/services/partners-service';

interface PartnerDashboardData {
  counts: {
    analysts: number;
  };
  credits: {
    totalCredits: number;
    currentPeriod: {
      userCredits: number;
      totalCreditsSent: number;
      chartData: Array<{ month: string; used: number }>;
    };
    previousPeriod: {
      totalCreditsSent: number;
      chartData: Array<{ month: string; used: number }>;
    };
    comparison: {
      creditsSentChange: number;
      averageUsageChange: number;
    };
  };
  assessments: {
    all: string;
    waiting: string;
    done: string;
  };
  invoices: {
    status: {
      paid: number;
      pending: number;
      cancelled: number;
    };
    metrics: {
      averageOrderValue: number;
      totalDiscounts: string;
      totalSales: number;
    };
    monthly: {
      months: string[];
      invoiceAmounts: number[];
      invoiceCounts: number[];
    };
  };
}

const MotionBox = motion(Box);

const PartnerDashboardPage: FC = () => {
  const toast = useToast();
  const { t } = useTranslation();
  const textColor = useColorModeValue('gray.800', 'white');
  const cardBg = useColorModeValue('white', 'gray.700');

  const [data, setData] = useState<PartnerDashboardData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getPartnerDashboard();
        setData(response.message);
      } catch (err) {
        toast({
          title: t('general.error'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [toast, t]);

  if (loading) {
    return (
      <Center h="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (!data) {
    return (
      <Center h="400px">
        <Text>{t('general.no_data_available')}</Text>
      </Center>
    );
  }

  // Prepare data for charts
  const monthlyInvoicesData = data.invoices.monthly.months.map((month, index) => ({
    month,
    amount: data.invoices.monthly.invoiceAmounts[index],
    count: data.invoices.monthly.invoiceCounts[index],
  }));



  const waitingCount = parseInt(data.assessments.waiting, 10) || 0;
  const doneCount = parseInt(data.assessments.done, 10) || 0;
  const totalCount = parseInt(data.assessments.all, 10) || 0;
  const otherCount = Math.max(0, totalCount - waitingCount - doneCount);

  const assessmentsStatusData = [
    { name: t('analyst.dashboard_funnel_step2'), value: waitingCount, color: '#ECC94B' },
    { name: t('analyst.dashboard_funnel_step3'), value: doneCount, color: '#68D391' },
    { name: t('analyst.dashboard_funnel_step4'), value: otherCount, color: '#4299e1' },
  ].filter(item => item.value > 0);

  // Fallback data if no assessments
  const hasAssessments = assessmentsStatusData.length > 0;

  return (
    <MotionBox
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      as={VStack}
      spacing={6}
      width="100%"
    >
      {/* Top Statistics */}
      <Grid mt={'20px'} templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={6} width="100%">
        <GridItem>
          <AnimatedCard p='15px' bg={cardBg} variants={cardVariants}>
            <Stat>
              <StatLabel color={textColor} fontSize="sm">
                {t('admin.dashboard_indicator_analysts')}
              </StatLabel>
              <StatNumber color={textColor} fontSize="2xl">
                {data.counts.analysts}
              </StatNumber>
            </Stat>
          </AnimatedCard>
        </GridItem>
        <GridItem>
          <AnimatedCard p='15px' bg={cardBg} variants={cardVariants}>
            <Stat>
              <StatLabel color={textColor} fontSize="sm">
                {t('admin.dashboard_indicator_credits_available')}
              </StatLabel>
              <StatNumber color={textColor} fontSize="2xl">
                {data.credits.totalCredits.toLocaleString()}
              </StatNumber>
            </Stat>
          </AnimatedCard>
        </GridItem>
        <GridItem>
          <AnimatedCard p='15px' bg={cardBg} variants={cardVariants}>
            <Stat>
              <StatLabel color={textColor} fontSize="sm">
                {t('admin.dashboard_indicator_assessments_sent')}
              </StatLabel>
              <StatNumber color={textColor} fontSize="2xl">
                {data.assessments.all}
              </StatNumber>
            </Stat>
          </AnimatedCard>
        </GridItem>
      </Grid>

      {/* Monthly Invoice Trends */}
      <VStack gap={4} w={'100%'} alignItems='stretch'>
        <Text mt='20px' color={textColor} fontSize='22px' fontWeight='700' lineHeight='100%'>
          {t('admin.dashboard_title')}
        </Text>
        <Panel padding={4}>
          <ResponsiveContainer width="100%" height="100%" minHeight="350px">
            <LineChart data={monthlyInvoicesData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              <Legend />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="amount"
                stroke="#4299e1"
                strokeWidth={2}
                name={t('invoices.amount')}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="count"
                stroke="#68D391"
                strokeWidth={2}
                name={t('invoices.count')}
              />
            </LineChart>
          </ResponsiveContainer>
        </Panel>
      </VStack>

      {/* Invoices and Assessments Row */}
      <Flex direction={{ base: 'column', lg: 'row' }} width="100%" gap={6}>
        <VStack gap={4} w={'100%'} alignItems='stretch'>
          <Text mt='20px' color={textColor} fontSize='22px' fontWeight='700' lineHeight='100%'>
            {t('general.invoices')}
          </Text>
          <Panel padding={4}>
            <ResponsiveContainer width="100%" height="100%" minHeight="350px">
              <BarChart data={monthlyInvoicesData}>
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#4299e1" name={t('invoices.count')} />
              </BarChart>
            </ResponsiveContainer>
          </Panel>
        </VStack>

        <VStack gap={4} w={'100%'} maxW={'600px'} alignItems='stretch'>
          <Text mt='20px' color={textColor} fontSize='22px' fontWeight='700' lineHeight='100%'>
            {t('analyst.assesments')}
          </Text>
          <Panel padding={4}>
            {hasAssessments ? (
              <ResponsiveContainer width="100%" height="100%" minHeight="350px">
                <PieChart>
                  <Pie
                    data={assessmentsStatusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(props) => {
                      const { name, percent } = props as { name?: string; percent?: number };
                      return `${name ?? ''} ${(percent ? percent * 100 : 0).toFixed(0)}%`;
                    }}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {assessmentsStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Center h="350px">
                <Text color={textColor}>{t('general.no_data_available')}</Text>
              </Center>
            )}
          </Panel>
        </VStack>
      </Flex>
    </MotionBox>
  );
};

export default PartnerDashboardPage; 